import {AxiosResponse} from 'axios';
import {ActivitiesGroup} from '../backend/interfaces/activities-group';
import {ActivitiesSector} from '../backend/interfaces/activities-sector';
import {ActivityType} from '../backend/interfaces/activity-type';
import {
  IndividualPlan,
  IndividualPlanDiagnostic,
  IndividualPlanDiagnosticGoal,
  IndividualPlanDiagnosticGoalObjective,
} from '../backend/interfaces/individual-plan';
import {IndividualPlanDiagnosticsBulkRequest} from '../backend/requests/individual-plan-diagnostics-bulk';
import {IndividualPlanHistoryEntry} from '../backend/interfaces/individual-plan-history';
import {IndividualPlanResults} from '../backend/responses/individual-plan-results';
import {SessionDatabase, SessionDatabaseStore} from '../databases/session';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export interface IndividualPlanCares {
  activitiesSectors: Array<ActivitiesSector>;
  activitiesGroups: Array<ActivitiesGroup>;
  activityTypes: Array<ActivityType>;
}

export class IndividualPlansRepository {
  // Singleton Instance
  private static _instance: IndividualPlansRepository;

  // Private
  private logger: Logger;

  static get instance(): IndividualPlansRepository {
    if (!this._instance) {
      this._instance = new IndividualPlansRepository();
    }
    return this._instance;
  }

  constructor() {
    this.logger = new Logger('IndividualPlansRepository');
  }

  private generateGetActiveIndividualPlansUrl(entityId: string, userId: string): string {
    return `/entities/${entityId}/users/${userId}/individual-plans?isActive=${true}`;
  }

  private async clearIndividualPlanCache(entityId: string, userId: string): Promise<void> {
    // Set url
    let url: string = this.generateGetActiveIndividualPlansUrl(entityId, userId);

    // Clear Session Database
    await SessionDatabase.instance.removeById(SessionDatabaseStore.UrlCache, url);
  }

  public async getActiveIndividualPlans(
    entityId: string,
    userId: string
  ): Promise<Array<IndividualPlan>> {
    // Set url
    let url: string = this.generateGetActiveIndividualPlansUrl(entityId, userId);

    // Check Session Database
    const urlCache = await SessionDatabase.instance.getById<Array<IndividualPlan>>(
      SessionDatabaseStore.UrlCache,
      url
    );
    if (!urlCache) {
      try {
        // Get users from backend
        const response: AxiosResponse<Array<IndividualPlan>> =
          await BackendApiManager.instance.get<Array<IndividualPlan>>(url);

        // Save in Session Database
        await SessionDatabase.instance.put(SessionDatabaseStore.UrlCache, url, response.data);

        // Done
        return response.data;
      } catch (error) {
        return [];
      }
    }

    return urlCache;
  }

  public async createIndividualPlan(individualPlan: IndividualPlan): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.post<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans`,
      individualPlan
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async updateIndividualPlan(individualPlan: IndividualPlan): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.put<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}`,
      individualPlan
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async archiveIndividualPlan(individualPlan: IndividualPlan): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.delete<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}`
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async createIndividualPlanDiagnostic(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic
  ): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.post<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics`,
      individualPlanDiagnostic
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  /**
   * Saves several diagnostics (with their goals, objectives and cares) in a single call: either
   * all of them are saved or none. Returns the whole updated plan.
   */
  public async bulkCreateIndividualPlanDiagnostics(
    individualPlan: IndividualPlan,
    request: IndividualPlanDiagnosticsBulkRequest
  ): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.post<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/bulk`,
      request
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async updateIndividualPlanDiagnostic(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic
  ): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.put<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}`,
      individualPlanDiagnostic
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async removeIndividualPlanDiagnostic(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic
  ): Promise<void> {
    await BackendApiManager.instance.delete<void>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}`
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);
  }

  public async createIndividualPlanDiagnosticGoal(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic,
    individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal
  ): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.post<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}/goals`,
      individualPlanDiagnosticGoal
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async updateIndividualPlanDiagnosticGoal(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic,
    individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal
  ): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.put<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}/goals/${individualPlanDiagnosticGoal.id}`,
      individualPlanDiagnosticGoal
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async removeIndividualPlanDiagnosticGoal(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic,
    individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal
  ): Promise<void> {
    await BackendApiManager.instance.delete<void>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}/goals/${individualPlanDiagnosticGoal.id}`
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);
  }

  public async createIndividualPlanDiagnosticGoalObjective(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic,
    individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal,
    individualPlanDiagnosticGoalObjective: IndividualPlanDiagnosticGoalObjective
  ): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.post<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}/goals/${individualPlanDiagnosticGoal.id}/objectives`,
      individualPlanDiagnosticGoalObjective
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async updateIndividualPlanDiagnosticGoalObjective(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic,
    individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal,
    individualPlanDiagnosticGoalObjective: IndividualPlanDiagnosticGoalObjective
  ): Promise<IndividualPlan> {
    const response = await BackendApiManager.instance.put<IndividualPlan>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}/goals/${individualPlanDiagnosticGoal.id}/objectives/${individualPlanDiagnosticGoalObjective.id}`,
      individualPlanDiagnosticGoalObjective
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);

    // Done
    return response.data;
  }

  public async removeIndividualPlanDiagnosticGoalObjective(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic,
    individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal,
    individualPlanDiagnosticGoalObjective: IndividualPlanDiagnosticGoalObjective
  ): Promise<void> {
    await BackendApiManager.instance.delete<void>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}/goals/${individualPlanDiagnosticGoal.id}/objectives/${individualPlanDiagnosticGoalObjective.id}`
    );

    // Clear Session Database
    await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);
  }

  /**
   * Lists the history of an individual plan (who changed what and when), latest first. The history
   * is never cached.
   *
   * @param before Only entries with a revision lower than this one (to get the next page)
   */
  public async getIndividualPlanHistory(
    individualPlan: Pick<IndividualPlan, 'id' | 'entityId' | 'userId'>,
    options: {limit?: number; before?: number} = {}
  ): Promise<Array<IndividualPlanHistoryEntry>> {
    const response = await BackendApiManager.instance.get<Array<IndividualPlanHistoryEntry>>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/history`,
      {params: options}
    );
    return response.data;
  }

  /**
   * Gets a single revision of the history of an individual plan, including the content (snapshot)
   * of the plan right after that change.
   */
  public async getIndividualPlanHistoryRevision(
    individualPlan: Pick<IndividualPlan, 'id' | 'entityId' | 'userId'>,
    revision: number
  ): Promise<IndividualPlanHistoryEntry> {
    const response = await BackendApiManager.instance.get<IndividualPlanHistoryEntry>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/history/${revision}`
    );
    return response.data;
  }

  /**
   * Restores the content of an individual plan to what it was on a previous revision. Nothing is
   * lost: a new revision is created.
   *
   * @param expectedRevision Current revision of the plan as seen by the user. If the plan changed in
   * the meantime the backend refuses the restore (409).
   */
  public async restoreIndividualPlanRevision(
    individualPlan: Pick<IndividualPlan, 'id' | 'entityId' | 'userId'>,
    revision: number,
    options: {expectedRevision?: number; reactivate?: boolean} = {}
  ): Promise<IndividualPlan> {
    try {
      const response = await BackendApiManager.instance.post<IndividualPlan>(
        `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/history/${revision}/restore`,
        options
      );
      return response.data;
    } finally {
      // Clear Session Database (also on a conflict, so the plan is loaded again)
      await this.clearIndividualPlanCache(individualPlan.entityId, individualPlan.userId);
    }
  }

  public async getIndividualPlanCares(
    individualPlan: IndividualPlan
  ): Promise<IndividualPlanCares | null> {
    // Set url
    let url: string = `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/cares`;

    // Check Session Database
    const urlCache = await SessionDatabase.instance.getById<IndividualPlanCares>(
      SessionDatabaseStore.UrlCache,
      url
    );
    if (!urlCache) {
      try {
        // Get users from backend
        const response: AxiosResponse<IndividualPlanCares> =
          await BackendApiManager.instance.get<IndividualPlanCares>(url);

        // Save in Session Database
        await SessionDatabase.instance.put(SessionDatabaseStore.UrlCache, url, response.data);

        // Done
        return response.data;
      } catch (error) {
        return null;
      }
    }

    return urlCache;
  }

  public async getIndividualPlanDiagnosticGoalObjectiveResults(
    individualPlan: IndividualPlan,
    individualPlanDiagnostic: IndividualPlanDiagnostic,
    individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal,
    individualPlanDiagnosticGoalObjective: IndividualPlanDiagnosticGoalObjective
  ): Promise<IndividualPlanResults> {
    const response = await BackendApiManager.instance.get<IndividualPlanResults>(
      `/entities/${individualPlan.entityId}/users/${individualPlan.userId}/individual-plans/${individualPlan.id}/diagnostics/${individualPlanDiagnostic.id}/goals/${individualPlanDiagnosticGoal.id}/objectives/${individualPlanDiagnosticGoalObjective.id}/results`
    );
    return response.data;
  }
}
